import { c as _c } from "react/compiler-runtime";
import { IconButton } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { getBrowseProjectsQueryKey, getBrowseProjectsQueryParams } from "#/features/project/lib/browseProjectsQuery";
import { projectBrowserSlice, selectCurrentPage, selectLastQueryKey, selectPageSize } from "#/features/project/model/projectBrowserSlice";
import { useProjectBrowserContext } from "#/features/project/ui/ProjectBrowser/ProjectBrowserContext";
import { useI18nContext } from "#/shared/hooks";
import { api } from "#/shared/lib";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
const BROWSE_PROJECTS_ERROR_KEY = "browse-projects-error";

/**
 * Hook for browsing projects with filters, sort, and pagination.
 * Encapsulates the browseProjects query, query key logic, Redux sync, and error snackbar.
 * Must be used within ProjectBrowserProvider.
 */
export const useBrowseProjects = () => {
  const $ = _c(56);
  const dispatch = useAppDispatch();
  const {
    searchQuery,
    selectedCategories,
    selectedDifficulties,
    showOnlyNew,
    sortBy,
    sortOrder
  } = useProjectBrowserContext();
  const currentPage = useAppSelector(selectCurrentPage);
  const pageSize = useAppSelector(selectPageSize);
  const lastQueryKey = useAppSelector(selectLastQueryKey);
  let t0;
  if ($[0] !== pageSize || $[1] !== searchQuery || $[2] !== selectedCategories || $[3] !== selectedDifficulties || $[4] !== showOnlyNew || $[5] !== sortBy || $[6] !== sortOrder) {
    t0 = {
      pageSize,
      searchQuery,
      selectedCategories,
      selectedDifficulties,
      showOnlyNew,
      sortBy,
      sortOrder
    };
    $[0] = pageSize;
    $[1] = searchQuery;
    $[2] = selectedCategories;
    $[3] = selectedDifficulties;
    $[4] = showOnlyNew;
    $[5] = sortBy;
    $[6] = sortOrder;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  const filterParams = t0;
  let t1;
  if ($[8] !== currentPage || $[9] !== filterParams) {
    t1 = {
      ...filterParams,
      page: currentPage
    };
    $[8] = currentPage;
    $[9] = filterParams;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const queryParams = t1;
  let t2;
  if ($[11] !== filterParams) {
    t2 = getBrowseProjectsQueryKey(filterParams);
    $[11] = filterParams;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const queryKey = t2;
  const allBrief = api.project.allBrief.useQuery();
  let t3;
  if ($[13] !== queryParams) {
    t3 = getBrowseProjectsQueryParams(queryParams);
    $[13] = queryParams;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const browseProjects = api.project.browseProjects.useQuery(t3);
  const error = browseProjects.error ?? allBrief.error ?? null;
  const {
    enqueueSnackbar,
    closeSnackbar
  } = useSnackbar();
  const {
    LL
  } = useI18nContext();
  let t4;
  if ($[15] !== allBrief || $[16] !== browseProjects) {
    t4 = () => {
      allBrief.refetch();
      browseProjects.refetch();
    };
    $[15] = allBrief;
    $[16] = browseProjects;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleRetry = t4;
  let t5;
  let t6;
  if ($[18] !== LL || $[19] !== closeSnackbar || $[20] !== enqueueSnackbar || $[21] !== error || $[22] !== handleRetry) {
    t5 = () => {
      if (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to load projects";
        enqueueSnackbar(errorMessage, {
          variant: "error",
          action: key => <IconButton size="small" onClick={() => {
            handleRetry();
            closeSnackbar(key);
          }} color="inherit" aria-label={LL.RETRY()}>{LL.RETRY()}</IconButton>,
          persist: false,
          autoHideDuration: 6000,
          key: BROWSE_PROJECTS_ERROR_KEY
        });
      }
    };
    t6 = [error, enqueueSnackbar, closeSnackbar, handleRetry, LL];
    $[18] = LL;
    $[19] = closeSnackbar;
    $[20] = enqueueSnackbar;
    $[21] = error;
    $[22] = handleRetry;
    $[23] = t5;
    $[24] = t6;
  } else {
    t5 = $[23];
    t6 = $[24];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[25] !== currentPage || $[26] !== dispatch || $[27] !== lastQueryKey || $[28] !== queryKey) {
    t7 = () => {
      if (queryKey !== lastQueryKey) {
        dispatch(projectBrowserSlice.actions.clearAccumulatedProjects());
        dispatch(projectBrowserSlice.actions.setLastQueryKey(queryKey));
        if (currentPage !== 1) {
          dispatch(projectBrowserSlice.actions.setCurrentPage(1));
        }
      }
    };
    t8 = [queryKey, lastQueryKey, currentPage, dispatch];
    $[25] = currentPage;
    $[26] = dispatch;
    $[27] = lastQueryKey;
    $[28] = queryKey;
    $[29] = t7;
    $[30] = t8;
  } else {
    t7 = $[29];
    t8 = $[30];
  }
  useEffect(t7, t8);
  let t9;
  if ($[31] !== browseProjects.data || $[32] !== currentPage || $[33] !== dispatch) {
    t9 = () => {
      if (!browseProjects.data?.projects) {
        return;
      }
      if (currentPage === 1) {
        dispatch(projectBrowserSlice.actions.setAccumulatedProjects(browseProjects.data.projects));
        return;
      }
      dispatch(projectBrowserSlice.actions.appendProjects(browseProjects.data.projects));
    };
    $[31] = browseProjects.data;
    $[32] = currentPage;
    $[33] = dispatch;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const t10 = browseProjects.data?.projects;
  let t11;
  if ($[35] !== currentPage || $[36] !== dispatch || $[37] !== t10) {
    t11 = [t10, currentPage, dispatch];
    $[35] = currentPage;
    $[36] = dispatch;
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  useEffect(t9, t11);
  let t12;
  let t13;
  if ($[39] !== allBrief.isLoading || $[40] !== browseProjects.isLoading || $[41] !== dispatch) {
    t12 = () => {
      dispatch(projectBrowserSlice.actions.setIsLoading(allBrief.isLoading || browseProjects.isLoading));
    };
    t13 = [allBrief.isLoading, browseProjects.isLoading, dispatch];
    $[39] = allBrief.isLoading;
    $[40] = browseProjects.isLoading;
    $[41] = dispatch;
    $[42] = t12;
    $[43] = t13;
  } else {
    t12 = $[42];
    t13 = $[43];
  }
  useEffect(t12, t13);
  let t14;
  if ($[44] !== browseProjects.data || $[45] !== dispatch) {
    t14 = () => {
      if (browseProjects.data?.hasMore !== undefined) {
        dispatch(projectBrowserSlice.actions.setHasMore(browseProjects.data.hasMore));
      }
    };
    $[44] = browseProjects.data;
    $[45] = dispatch;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const t15 = browseProjects.data?.hasMore;
  let t16;
  if ($[47] !== dispatch || $[48] !== t15) {
    t16 = [t15, dispatch];
    $[47] = dispatch;
    $[48] = t15;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  useEffect(t14, t16);
  const t17 = allBrief.isLoading || browseProjects.isLoading;
  let t18;
  if ($[50] !== allBrief || $[51] !== browseProjects || $[52] !== error || $[53] !== handleRetry || $[54] !== t17) {
    t18 = {
      isLoading: t17,
      allBrief,
      browseProjects,
      error,
      handleRetry
    };
    $[50] = allBrief;
    $[51] = browseProjects;
    $[52] = error;
    $[53] = handleRetry;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  return t18 as const;
};